<template>
  <div id="app">
    <!-- 全局樱花瓣特效层 - 固定定位，覆盖整个屏幕 -->
    <CherryBlossoms :intensity="globalMissIntensity" />

    <!-- 全屏确认气泡特效层 -->
    <ReceiveConfirmBubbles ref="confirmBubbles" />

    <!-- 底部悬浮导航 - 智能隐藏 -->
    <nav v-if="$route.name !== 'login'" class="healing-nav" :class="{ 'nav-hidden': hideNavigation }" v-show="!hideNavigation">
      <!-- 统一胶囊导航栏 -->
      <div class="healing-nav-bar">
        <router-link to="/" exact>首页</router-link>
        <router-link to="/message">留言</router-link>
        <router-link to="/orchard">果园</router-link>
        <button class="logout-nav-btn" @click="handleLogoutClick" :class="{ 'logout-pending': logoutPending }">
          <svg class="logout-icon" viewBox="0 0 24 24" width="18" height="18">
            <path fill="currentColor" d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
          </svg>
        </button>
      </div>
    </nav>
    <router-view/>
  </div>
</template>

<script>
import { removeToken, getToken } from '@/utils/token'
import CherryBlossoms from '@/components/CherryBlossoms.vue'
import ReceiveConfirmBubbles from '@/components/ReceiveConfirmBubbles.vue'
import missStorage from '@/utils/missStorage.js'
import { getMissStatus } from '@/api/miss.js'

export default {
  name: 'App',
  components: {
    CherryBlossoms,
    ReceiveConfirmBubbles
  },
  data () {
    return {
      // 导航相关
      logoutPending: false,
      logoutTimer: null,
      hideNavigation: false,

      // 全局思念状态
      globalMissIntensity: 0, // 全局樱花瓣强度

      // WebSocket连接
      websocket: null,
      isConnected: false,
      heartbeatTimer: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: 3
    }
  },

  mounted () {
    // 导航相关事件监听
    this.$root.$on('show-popup', () => {
      this.hideNavigation = true
    })
    this.$root.$on('hide-popup', () => {
      this.hideNavigation = false
    })

    // 初始化全局思念系统
    this.initGlobalMissSystem()
  },

  beforeDestroy () {
    // 清理定时器
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer)
    }

    // 清理事件监听
    this.$root.$off('show-popup')
    this.$root.$off('hide-popup')
    this.$root.$off('update-miss-intensity')
    this.$root.$off('send-miss-request')
    this.$root.$off('confirm-miss-request')
    this.$root.$off('request-global-miss-intensity')

    // 清理WebSocket
    this.cleanupWebSocket()
  },

  methods: {
    // ==================== 导航相关方法 ====================
    handleLogoutClick () {
      if (this.logoutPending) {
        this.executeLogout()
      } else {
        this.startLogoutCountdown()
      }
    },

    startLogoutCountdown () {
      this.logoutPending = true
      this.$toast({
        message: '双击确认退出 (3s内有效) ',
        duration: 3000,
        className: 'logout-toast'
      })
      this.logoutTimer = setTimeout(() => {
        this.logoutPending = false
        this.logoutTimer = null
      }, 3000)
    },

    executeLogout () {
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer)
        this.logoutTimer = null
      }
      this.logoutPending = false

      this.$toast({
        message: '已安全退出登录 👋',
        duration: 1500,
        className: 'logout-success-toast'
      })

      setTimeout(() => {
        removeToken()
        localStorage.removeItem('userId')
        localStorage.removeItem('nickname')
        this.$router.replace({ name: 'login' })
      }, 800)
    },

    // ==================== 全局思念系统 ====================
    initGlobalMissSystem () {
      // 只在非登录页面初始化
      if (this.$route.name === 'login') return

      // 加载本地思念状态
      this.loadGlobalMissData()

      // 初始化WebSocket连接
      this.initWebSocket()

      // 监听来自其他组件的事件
      this.$root.$on('update-miss-intensity', this.updateMissIntensity)
      this.$root.$on('send-miss-request', this.handleSendMissRequest)
      this.$root.$on('confirm-miss-request', this.handleConfirmMissRequest)
      this.$root.$on('request-global-miss-intensity', this.handleGlobalMissIntensityRequest)
    },

    // 加载全局思念数据
    async loadGlobalMissData () {
      try {
        // 先加载本地数据
        const localData = missStorage.loadMissData()
        this.globalMissIntensity = localData.receivedMissIntensity || 0

        // 从服务器同步最新状态
        const serverStatus = await getMissStatus()
        if (serverStatus.data) {
          this.globalMissIntensity = serverStatus.data.receivedMissIntensity || 0
          this.saveGlobalMissData()
        }
      } catch (error) {
        console.error('加载全局思念数据失败:', error)
      }
    },

    // 保存全局思念数据
    saveGlobalMissData () {
      const data = {
        receivedMissIntensity: this.globalMissIntensity
      }
      missStorage.saveMissData(data)
    },

    // 更新思念强度（供其他组件调用）
    updateMissIntensity (newIntensity) {
      this.globalMissIntensity = newIntensity
      this.saveGlobalMissData()
    },

    // 处理发送思念请求（供其他组件调用）
    handleSendMissRequest () {
      // 发送思念的逻辑由具体页面处理，这里只是转发
      this.$root.$emit('global-send-miss-request')
    },

    // 处理确认思念请求（供其他组件调用）
    handleConfirmMissRequest (data) {
      this.globalMissIntensity = data.newIntensity
      this.saveGlobalMissData()
      // 转发给具体页面处理
      this.$root.$emit('global-confirm-miss-request', data)
    },

    // 处理全局状态请求
    handleGlobalMissIntensityRequest () {
      // 响应状态请求，发送当前全局强度
      this.$root.$emit('global-miss-intensity-response', this.globalMissIntensity)
      console.log('App: 响应全局状态请求，当前强度:', this.globalMissIntensity)
    },

    // ==================== WebSocket连接管理 ====================
    initWebSocket () {
      const token = getToken()
      if (!token) {
        console.error('用户未登录，无法建立WebSocket连接')
        return
      }

      this.cleanupWebSocket()

      try {
        // TODO: 这里是上线的websocket，测试时用下面的
        const wsUrl = `wss://yhlljj.com/websocket?token=${token}`
        // TODO: 这里测试本地的websocket，上线时要注销用上面的
        // const wsUrl = `ws://localhost:8086/websocket?token=${token}`
        this.websocket = new WebSocket(wsUrl)

        this.websocket.onopen = () => {
          console.log('全局WebSocket连接已建立')
          this.isConnected = true
          this.reconnectAttempts = 0
          this.startHeartbeat()
        }

        this.websocket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleWebSocketMessage(message)
          } catch (error) {
            console.error('解析WebSocket消息失败:', error)
          }
        }

        this.websocket.onclose = () => {
          console.log('全局WebSocket连接已关闭')
          this.isConnected = false
          this.stopHeartbeat()

          // 简化的自动重连
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++
            setTimeout(() => {
              if (!this.isConnected && this.$route.name !== 'login') {
                this.initWebSocket()
              }
            }, 3000)
          }
        }

        this.websocket.onerror = (error) => {
          console.error('全局WebSocket连接错误:', error)
          this.isConnected = false
        }
      } catch (error) {
        console.error('创建全局WebSocket连接失败:', error)
      }
    },

    // 处理WebSocket消息
    handleWebSocketMessage (message) {
      console.log('收到全局WebSocket消息:', message)

      switch (message.type) {
        case 'new_miss': {
          // 收到新思念 - 更新全局樱花瓣强度
          const intensity = message.intensity || 1
          this.globalMissIntensity += intensity
          this.saveGlobalMissData()
          console.log(`全局收到新思念，强度: ${intensity}，总强度: ${this.globalMissIntensity}`)

          // 通知具体页面更新
          this.$root.$emit('global-new-miss-received', {
            intensity,
            totalIntensity: this.globalMissIntensity
          })
          break
        }

        case 'received_confirmed':
          // 对方确认收到了我的思念 - 显示全屏气泡特效
          if (this.$refs.confirmBubbles) {
            this.$refs.confirmBubbles.showBubble('对方收到了你的想念')
          }

          // 通知具体页面
          this.$root.$emit('global-miss-confirmed', {
            timestamp: message.timestamp
          })
          break

        case 'pong':
          // 心跳响应
          console.log('全局心跳正常')
          break

        default:
          console.log('未知全局消息类型:', message.type)
      }
    },

    // 清理WebSocket连接
    cleanupWebSocket () {
      this.stopHeartbeat()
      if (this.websocket) {
        this.websocket.onopen = null
        this.websocket.onmessage = null
        this.websocket.onclose = null
        this.websocket.onerror = null
        if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
          this.websocket.close()
        }
        this.websocket = null
      }
    },

    // 开始心跳
    startHeartbeat () {
      this.stopHeartbeat()
      this.heartbeatTimer = setInterval(() => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.send('ping')
        }
      }, 30000)
    },

    // 停止心跳
    stopHeartbeat () {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
    }
  },

  // 监听路由变化
  watch: {
    '$route' (to, from) {
      // 从登录页进入其他页面时，初始化思念系统
      if (from.name === 'login' && to.name !== 'login') {
        this.$nextTick(() => {
          this.initGlobalMissSystem()
        })
      }

      // 进入登录页时，清理WebSocket连接
      if (to.name === 'login') {
        this.cleanupWebSocket()
        this.globalMissIntensity = 0
      }
    }
  }
}
</script>

<style scoped>
/* 为页面内容添加底部间距，避免被导航栏遮挡 */
#app {
  padding-bottom: 100px; /* 给底部导航留出空间 */
}

/* 底部悬浮导航容器 */
.healing-nav {
  position: fixed;
  bottom: 15px; /* 更贴近底部 */
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 🎭 智能隐藏动画 - 苹果级过渡效果 */
.healing-nav.nav-hidden {
  transform: translateX(-50%) translateY(120px); /* 向下滑出屏幕 */
  opacity: 0;
  pointer-events: none; /* 禁用交互 */
}

/* 统一胶囊式导航栏 - 苹果磨砂玻璃风格 */
.healing-nav-bar {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 8px;
  background: rgba(255, 251, 245, 0.3); /* 温暖奶白半透明 */
  backdrop-filter: blur(20px) saturate(180%);
  border-radius: 50px;
  border: 1px solid rgba(255, 239, 213, 0.3); /* 温暖边框 */
  box-shadow:
    0 8px 32px rgba(255, 183, 153, 0.15), /* 温暖阴影 */
    0 2px 8px rgba(255, 206, 176, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8); /* 更强内光 */
}

/* 导航链接通用样式 */
.healing-nav-bar a,
.healing-nav-bar .logout-nav-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 50px;
  font-weight: 500;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.3px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
  min-width: 80px; /* 增加最小宽度，适应中文字符 */
  white-space: nowrap; /* 强制文字单行显示，防止换行 */
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
}

/* 普通导航链接颜色 - 温暖色调 */
.healing-nav-bar a {
  color: #8b7355; /* 温暖棕灰 */
}

/* 退出按钮特殊颜色 */
.healing-nav-bar .logout-nav-btn {
  color: #a0826d; /* 温暖退出色 */
}

/* 激活状态渐变背景 - 温暖治愈系 */
.healing-nav-bar a::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(255, 183, 153, 0.85) 0%,
    rgba(255, 206, 176, 0.9) 50%,
    rgba(255, 228, 196, 0.85) 100%
  ); /* 温暖粉橙渐变，保持透明度让文字可见 */
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50px;
  box-shadow: 0 2px 12px rgba(255, 183, 153, 0.3); /* 温暖发光效果 */
}

/* 退出按钮特殊背景 - 温暖警告色 */
.healing-nav-bar .logout-nav-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(255, 154, 118, 0.85) 0%,
    rgba(255, 134, 94, 0.9) 100%
  ); /* 温暖橙红渐变 */
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50px;
  box-shadow: 0 2px 12px rgba(255, 154, 118, 0.3); /* 温暖发光 */
}

.healing-nav-bar a span,
.healing-nav-bar .logout-nav-btn span {
  position: relative;
  z-index: 1;
}

/* 激活状态 - 优化文字对比度 */
.healing-nav-bar a.router-link-exact-active {
  color: #8b4513; /* 深棕色，在温暖背景上有良好对比度 */
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8); /* 轻微文字发光 */
}

.healing-nav-bar a.router-link-exact-active::before {
  opacity: 1;
}

/* 悬浮效果 - 普通导航，温暖治愈系 */
.healing-nav-bar a:hover:not(.router-link-exact-active) {
  color: #d2691e; /* 温暖橙棕色 */
  background: rgba(255, 183, 153, 0.15); /* 温暖半透明背景 */
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(255, 183, 153, 0.2); /* 温暖悬浮阴影 */
}

/* 悬浮效果 - 退出按钮 */
.healing-nav-bar .logout-nav-btn:hover {
  color: #8b4513; /* 深棕色文字 */
  transform: translateY(-2px);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8); /* 文字发光 */
}

.healing-nav-bar .logout-nav-btn:hover::before {
  opacity: 1;
}

/* 💫 退出按钮等待状态视觉反馈 */
.healing-nav-bar .logout-nav-btn.logout-pending {
  background: rgba(255, 154, 118, 0.2) !important; /* 温暖橙色背景 */
  color: #ff6b35 !important; /* 橙色文字 */
  animation: logoutPulse 1s ease-in-out infinite; /* 脉冲动画 */
  box-shadow: 0 0 0 2px rgba(255, 154, 118, 0.3); /* 发光边框 */
}

/* 脉冲动画 */
@keyframes logoutPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

.healing-nav-bar .logout-nav-btn:active {
  transform: translateY(0);
}

/* 退出图标动画效果 */
.logout-icon {
  transition: transform 0.2s ease;
}

.healing-nav-bar .logout-nav-btn:hover .logout-icon {
  transform: rotate(-15deg) scale(1.1);
}

/* 响应式设计 */
@media (max-width: 768px) {
  #app {
    padding-bottom: 90px;
  }

  .healing-nav {
    bottom: 12px; /* 更贴近底部 */
  }

  .healing-nav-bar {
    gap: 4px;
    padding: 6px;
  }

  .healing-nav-bar a,
  .healing-nav-bar .logout-nav-btn {
    padding: 8px 14px;
    font-size: 0.85rem;
    min-width: 50px;
  }
}

@media (max-width: 480px) {
  #app {
    padding-bottom: 80px;
  }

  .healing-nav {
    bottom: 6px; /* 最贴近底部 */
  }

  .healing-nav-bar a,
  .healing-nav-bar .logout-nav-btn {
    padding: 7px 12px;
    font-size: 0.8rem;
  }
}

/* 针对不同页面的特殊处理 */
@media (max-height: 600px) {
  .healing-nav {
    bottom: 12px;
  }
}

/* 🎨 自定义Toast样式 */
::v-deep .logout-toast {
  background: linear-gradient(135deg, #ff9a76 0%, #ff6b35 100%) !important;
  color: white !important;
  border-radius: 25px !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 8px 24px rgba(255, 154, 118, 0.3) !important;
}

::v-deep .logout-success-toast {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%) !important;
  color: white !important;
  border-radius: 25px !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 8px 24px rgba(74, 222, 128, 0.3) !important;
}
</style>
